import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import type { ApiResponse } from '@evi/shared';
import { api } from '@/lib/api';
import type { NavSection } from '@/config/nav';
import {
  filterJumpPages, isQuickJumpShortcut, jumpKey, jumpPages, lookupPastedId, pastedId, quickJumpEnabled,
  type JumpItem, type JumpLookup, type JumpOption, type JumpType,
} from '@/lib/quickJump';
import styles from './QuickJump.module.css';

/**
 * Ctrl+K quick jump (Cmd+K on macOS; Warren, 21 October 2026, item M). Lists the pages of the person's own menu, filtered
 * as they type; arrows and Enter go, Escape closes. A pasted 24 character id opens a job, PO, store order or charger the
 * Api lets them see. Mounted by TopNav with the menu it renders, and only for a menu of more than 5 pages.
 */
const TYPE_NAME: Record<JumpType, string> = { job: 'Job', po: 'PO', order: 'Store order', charger: 'Charger' };
const read = <T,>(path: string) => api.get(path, { retry: 0, timeout: 10_000 }).json<ApiResponse<T>>().then(r => r.data ?? null);
const LOOKUPS: Record<JumpType, JumpLookup> = {
  job: async id => {
    const job = (await read<{ job: { number?: string; customer?: { business_name?: string } } }>(`admin/partner-jobs/${id}`))?.job;
    return job ? ['Job', job.number, job.customer?.business_name].filter(Boolean).join(' ') : null;
  },
  po: async id => { const po = await read<{ po_number?: string }>(`procurement/${id}`); return po ? `PO ${po.po_number ?? ''}`.trim() : null; },
  order: async id => { const o = await read<{ order_number?: string }>(`admin/orders/${id}`); return o ? `Store order ${o.order_number ?? ''}`.trim() : null; },
  charger: async id => { const c = await read<{ identity?: string }>(`chargers/${id}`); return c ? `Charger ${c.identity ?? ''}`.trim() : null; },
};

export function QuickJump({ sections, onNavigate }: { sections: NavSection[]; onNavigate: (href: string) => void }) {
  const pages = useMemo(() => jumpPages(sections), [sections]);
  const enabled = quickJumpEnabled(pages);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const [found, setFound] = useState<{ id: string; items: JumpItem[] } | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const mac = /Mac|iPhone|iPad/.test(navigator.platform);
    const onKey = (e: KeyboardEvent) => {
      if (!isQuickJumpShortcut(e, mac)) return;
      // A rich text editor keeps its own Ctrl+K (insert a link).
      if (e.target instanceof HTMLElement && e.target.isContentEditable) return;
      e.preventDefault();
      setOpen(o => {
        if (!o) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        return !o;
      });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);

  useEffect(() => {
    if (open) { setQuery(''); setIndex(0); setFound(null); return; }
    returnFocus.current?.focus();
    returnFocus.current = null;
  }, [open]);

  const id = pastedId(query);
  useEffect(() => {
    if (!id) return;
    let live = true;
    void lookupPastedId(id, pages, LOOKUPS).then(items => { if (live) { setFound({ id, items }); setIndex(0); } });
    return () => { live = false; };
  }, [id, pages]);

  const options: JumpOption[] = id ? (found?.id === id ? found.items : []) : filterJumpPages(pages, query);
  const active = Math.min(index, Math.max(options.length - 1, 0));
  useEffect(() => { listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  if (!enabled || !open) return null;
  const go = (o: JumpOption | undefined) => { if (!o) return; setOpen(false); onNavigate(o.href); };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const k = jumpKey(e.key, active, options.length);
    if (k.type === 'none') return;
    e.preventDefault();
    if (k.type === 'close') setOpen(false);
    else if (k.type === 'move') setIndex(k.index);
    else go(options[k.index]);
  };
  const status = id
    ? (found?.id !== id ? 'Looking up that id.' : options.length ? '' : 'No job, PO, store order or charger you can open has that id.')
    : options.length ? '' : 'No page in your menu matches.';

  return <div className={styles.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
    <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Quick jump">
      <div className={styles.search}>
        <Search size={16} aria-hidden />
        <input autoFocus className={styles.input} value={query} onChange={e => { setQuery(e.target.value); setIndex(0); }} onKeyDown={onKeyDown}
          placeholder="Jump to a page, or paste an id" aria-label="Jump to a page, or paste an id" role="combobox" aria-expanded="true"
          aria-controls="quick-jump-list" aria-activedescendant={options.length ? `quick-jump-${active}` : undefined} autoComplete="off" spellCheck={false} />
      </div>
      <ul id="quick-jump-list" ref={listRef} className={styles.list} role="listbox" aria-label="Pages">
        {options.map((o, i) => <li key={o.kind === 'page' ? o.href : `${o.type}:${o.href}`} id={`quick-jump-${i}`} data-index={i} role="option" aria-selected={i === active}
          className={styles.option} data-active={i === active || undefined} onMouseMove={() => setIndex(i)} onMouseDown={e => { e.preventDefault(); go(o); }}>
          <span className={styles.label}>{o.label}</span>
          <span className={styles.where}>{o.kind === 'page' ? o.section : TYPE_NAME[o.type]}</span>
        </li>)}
      </ul>
      {status && <p className={styles.status} role="status">{status}</p>}
      <p className={styles.keys}>Arrows to move, Enter to open, Escape to close.</p>
    </div>
  </div>;
}
